/*
 * WeatherTileRenderer — outside conditions the heating curve is running on.
 *
 * Hero: outside temperature. Sub-line explains what the curve is currently
 * computing with (gedämpfte Temperatur), which is the number the controller
 * actually uses for its heating curve. Chip strip carries humidity + wind.
 * Weather icon in the top-right maps a small enum to a Unicode glyph; the
 * MVP prefers Unicode over inline SVG per the widget brief.
 */

import type { WeatherTileWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import {
  usePoint,
  useOptionalPoint,
  numeric,
  quality,
} from './_compound-helpers';
import { formatNumber } from '../../format/leafValue';
import './WeatherTileRenderer.css';

interface Props {
  widget: WeatherTileWidget;
  source: IPointBindingSource;
}

export function WeatherTileRenderer({ widget, source }: Props) {
  const outside = usePoint(source, widget.outsideTemperaturePoint);
  const attenuated = useOptionalPoint(source, widget.attenuatedTemperaturePoint);
  const humidity = useOptionalPoint(source, widget.humidityPoint);
  const wind = useOptionalPoint(source, widget.windSpeedPoint);
  const icon = useOptionalPoint(source, widget.weatherIconPoint);

  const outsideVal = numeric(outside.value.value?.value);
  const attenuatedVal = numeric(attenuated?.value.value?.value);
  const humidityVal = numeric(humidity?.value.value?.value);
  const windVal = numeric(wind?.value.value?.value);
  const iconRaw = icon?.value.value?.value;

  const outsideStale = quality(outside.value.value) !== 'ok';
  // Weather tile is basically "outside temp"; be conservative and dim only
  // when the primary reading is bad or stale on its own.
  const tileStale = outsideStale;

  const glyph = weatherGlyph(iconRaw);

  return (
    <div class={`hv-weather${tileStale ? ' hv-weather--stale' : ''}`}>
      <header class="hv-weather__head">
        <div class="hv-weather__title">
          <div class="hv-weather__name">Wetter</div>
          {widget.label && <div class="hv-weather__label">{widget.label}</div>}
        </div>
        {glyph && <span class="hv-weather__icon" aria-label={glyph.label}>{glyph.glyph}</span>}
      </header>

      <div class="hv-weather__body">
        <div class="hv-weather__hero">
          <div class="hv-weather__hero-value">
            {outsideVal != null ? formatNumber(outsideVal, 1) : '—'}<span>°C</span>
          </div>
          <div class="hv-weather__hero-label">Außen</div>
          {attenuatedVal != null && (
            <div class="hv-weather__attenuated">
              Kurve rechnet mit <span class="hv-weather__attenuated-num">{formatNumber(attenuatedVal, 1)} °C</span>
            </div>
          )}
        </div>
      </div>

      <footer class="hv-weather__foot">
        {humidityVal != null && (
          <span class="hv-weather__stat">
            <span class="hv-weather__stat-label">Feuchte</span>
            <span class="hv-weather__stat-value">{Math.round(humidityVal)} %</span>
          </span>
        )}
        {windVal != null && (
          <span class="hv-weather__stat">
            <span class="hv-weather__stat-label">Wind</span>
            <span class="hv-weather__stat-value">{formatNumber(windVal, 0)} km/h</span>
          </span>
        )}
      </footer>
    </div>
  );
}

// ---------- Weather-icon mapping ----------------------------------------

interface Glyph { glyph: string; label: string; }

function weatherGlyph(v: unknown): Glyph | null {
  const raw = String(v ?? '').trim().toLowerCase();
  if (raw === 'sonnig' || raw === 'sunny' || raw === 'sun') return { glyph: '☀', label: 'sonnig' };
  if (raw === 'bewoelkt' || raw === 'bewölkt' || raw === 'cloudy' || raw === 'clouds') return { glyph: '☁', label: 'bewölkt' };
  if (raw === 'regen' || raw === 'rain' || raw === 'rainy') return { glyph: '☔', label: 'Regen' };
  if (raw === 'schnee' || raw === 'snow' || raw === 'snowy') return { glyph: '❄', label: 'Schnee' };
  return null;
}
